import { apiJobs } from "@/api/recruit/job.api.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { JobViewModal } from "@/pages/recruit/jobs/widgets/JobViewModal.tsx";
import { useRecruitingReports } from "@/pages/reporting/recruiting/recruiting-reports.hook.ts";
import {
  ApiAnalyticsGroupBy,
  ApiAnalyticsRecruitingFilter,
  ApiAnalyticsRecruitingStageFlowReport,
  ApiAnalyticsRecruitingStageFlowReportRowItem,
  ApiAnalyticsValueMode,
} from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiJobShort, JobStatus } from "@/types/recruit/job.types.ts";
import {
  Box,
  DotSeparator,
  FontAwesomeIcon,
  LinkButtonTo,
  LinkTo,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface RecruitingStageFlowReportTableProps {
  report: ApiAnalyticsRecruitingStageFlowReport | undefined;
  filter: ApiAnalyticsRecruitingFilter;
  isLoading: boolean;
}

interface VMRecruitingStageFlowReportRow {
  title?: string;
  caption?: string;
  link?: string;
  onClick?: () => Promise<void>;
  isGroup: boolean;
  isFooter?: boolean;
  stages: ApiAnalyticsRecruitingStageFlowReportRowItem[];
  employee?: ApiEmployeeShort;
  job?: ApiJobShort;
  period?: string;
}

export const RecruitingStageFlowReportTable = ({
  report,
  filter,
  isLoading,
}: RecruitingStageFlowReportTableProps) => {
  const { renderEmployeeName, formatPeriodDates, renderDate, renderTimeAgo } = useFormatter();
  const { t } = useTranslation();
  const { handlePreviewApplicants } = useRecruitingReports();
  const { openModal } = useModals();

  const renderJobStatusIcon = (job?: ApiJobShort) => {
    switch (job?.status) {
      case JobStatus.archived:
        return <FontAwesomeIcon icon={"fa-light fa-archive"} />;
      case JobStatus.on_hold:
        return <FontAwesomeIcon icon={"fa-regular fa-pause"} />;
    }
  };

  const cellWrap = (content: React.ReactNode) => (
    <div className={"flex h-[72px] items-center overflow-hidden"}>{content}</div>
  );

  const renderCreatedTooltip = (mainText: string, job?: ApiJobShort) => {
    const createdAt = job?.datetime_created_at;
    return (
      <Stack gap={"sm"}>
        <Text className={"font-semibold"}>{mainText}</Text>
        {createdAt != null && (
          <DotSeparator>
            <Text className={"font-semibold"}>
              {`${t("common:created")} ${renderDate(createdAt)}`}
            </Text>
            <Text>{renderTimeAgo(createdAt)}</Text>
          </DotSeparator>
        )}
      </Stack>
    );
  };

  const renderTitleCell = ({
    text,
    clampClass,
    showJobIcon = false,
    job,
    link,
    onClick,
    tooltip,
  }: {
    text: string;
    clampClass: string;
    showJobIcon?: boolean;
    job?: ApiJobShort;
    link?: string;
    onClick?: () => Promise<void>;
    tooltip?: React.ReactNode;
  }) => {
    const titleEl = (
      <Title
        size={"sm"}
        header={text}
        headerIcon={showJobIcon && renderJobStatusIcon(job)}
        className={twMerge(
          clampClass,
          "break-words min-w-0",
          showJobIcon && job?.status == JobStatus.archived ? "opacity-50" : ""
        )}
      />
    );

    const trigger = onClick ? (
      <LinkButtonTo variant={"secondary"} onClick={onClick} className={"block w-full"}>
        {titleEl}
      </LinkButtonTo>
    ) : link ? (
      <LinkTo to={link} variant={"secondary"} target={"_blank"} className={"block w-full"}>
        {titleEl}
      </LinkTo>
    ) : (
      titleEl
    );

    if (!tooltip) return cellWrap(trigger);

    return cellWrap(
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={"w-full min-w-0"}>{trigger}</div>
        </TooltipTrigger>
        <TooltipContent className={"max-w-[320px]"}>{tooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const reportCalculated = useMemo(() => {
    if (!report || report.report.rows.length == 0) return [];

    const jobs: ApiJobShort[] = [];
    for (let i = 0; i < report.report.rows.length; i++) {
      const item = report.report.rows[i];

      const job = jobs.find((x) => x.id === item.job?.id);
      if (!job && item.job) {
        jobs.push(item.job);
      }
    }

    const recruiters: ApiEmployeeShort[] = [];
    for (let i = 0; i < report.report.rows.length; i++) {
      const item = report.report.rows[i];

      const recruiter = recruiters.find((x) => x.id === item.employee?.id);
      if (!recruiter && item.employee) {
        recruiters.push(item.employee);
      }
    }

    // const allStagesFlatMap = report.report.rows.flatMap((x) => x.stages);
    const result: VMRecruitingStageFlowReportRow[] = [];

    if (filter.group_by == null || filter.group_by == ApiAnalyticsGroupBy.none) {
      let prevDate: string | undefined = undefined;
      for (let i = 0; i < report.report.rows.length; i++) {
        const item = report.report.rows[i];

        const caption = formatPeriodDates({
          dateStart: item.date_report_from,
          dateEnd: item.date_report_to,
        })?.title;

        if (!prevDate || prevDate != caption) {
          prevDate = caption;

          result.push({
            title: caption,
            // caption: null,
            isGroup: true,
            stages: [],
          });
        }

        result.push({
          title: item.employee ? renderEmployeeName(item.employee) : "",
          caption: item.job?.title,
          link: item.job?.id ? `/recruitment/candidates/?job_id=${item.job.id}` : undefined,
          isGroup: false,
          employee: item.employee,
          job: item.job,
          stages: item.stages,
        });
      }
    } else if (filter.group_by == ApiAnalyticsGroupBy.job) {
      let prevDate: string | undefined = undefined;
      for (let i = 0; i < report.report.rows.length; i++) {
        const item = report.report.rows[i];

        const caption = formatPeriodDates({
          dateStart: item.date_report_from,
          dateEnd: item.date_report_to,
        })?.title;

        if (!prevDate || prevDate != caption) {
          prevDate = caption;

          result.push({
            title: caption,
            isGroup: true,
            stages: [],
          });
        }

        result.push({
          title: item.job ? item.job.title : "",
          onClick: async () => {
            if (item.job) {
              const { data: jobDetails } = await apiJobs.getById(item.job.id);
              if (jobDetails) openModal(JobViewModal, { job: jobDetails });
            }
          },
          isGroup: false,
          employee: item.employee,
          job: item.job,
          stages: item.stages,
        });
      }
    } else {
      let prevDate: string | undefined = undefined;
      for (let i = 0; i < report.report.rows.length; i++) {
        const item = report.report.rows[i];

        const caption = formatPeriodDates({
          dateStart: item.date_report_from,
          dateEnd: item.date_report_to,
        })?.title;

        if (!prevDate || prevDate != caption) {
          prevDate = caption;

          result.push({
            title: caption,
            // caption: null,
            isGroup: true,
            stages: [],
          });
        }

        result.push({
          title: item.employee ? renderEmployeeName(item.employee) : t("ats:career_sites"),
          caption: "",
          isGroup: false,
          employee: item.employee,
          job: item.job,
          stages: item.stages,
        });
      }
    }

    return result;
  }, [report, filter.group_by]);

  const columnHelper = createColumnHelper<VMRecruitingStageFlowReportRow>();
  const columns = useMemo<ColumnDef<VMRecruitingStageFlowReportRow>[]>(() => {
    const allStagesFlatMap = report ? report.report.rows.flatMap((x) => x.stages) : [];

    const inner: DisplayColumnDef<VMRecruitingStageFlowReportRow, unknown>[] = [];
    const isTotalMode = filter.value_mode === ApiAnalyticsValueMode.total;

    const isDateGrouping = filter.group_by == null || filter.group_by == ApiAnalyticsGroupBy.none;

    const totalFooter = () =>
      isTotalMode ? (
        <Box className="pt-8">
          <Title size={"md"} header={t("common:total")} caption={""} paddingBottom={true} />
        </Box>
      ) : undefined;

    const renderGroupTitle = (rowData: VMRecruitingStageFlowReportRow) => (
      <Title
        size={"md"}
        header={rowData.title || ""}
        caption={rowData.caption}
        paddingBottom={true}
        paddingTop={true}
      />
    );

    if (isDateGrouping) {
      inner.push(
        columnHelper.display({
          id: "name",
          header: t("core:name"),
          cell: (cell) =>
            cell.row.original.isGroup
              ? renderGroupTitle(cell.row.original)
              : renderTitleCell({
                  text: cell.row.original.title || "",
                  clampClass: "line-clamp-2",
                }),
          footer: totalFooter,
          ...ColumnSizes.stageFlowName,
        })
      );

      inner.push(
        columnHelper.display({
          id: "vacancy",
          header: t("ats:job"),
          cell: (cell) =>
            cell.row.original.isGroup
              ? null
              : renderTitleCell({
                  text: cell.row.original.caption || "",
                  clampClass: "line-clamp-3",
                  showJobIcon: true,
                  job: cell.row.original.job,
                  link: cell.row.original.link,
                  tooltip: cell.row.original.caption
                    ? renderCreatedTooltip(cell.row.original.caption, cell.row.original.job)
                    : undefined,
                }),
          ...ColumnSizes.stageFlowVacancy,
        })
      );
    } else {
      inner.push(
        columnHelper.display({
          id: "week",
          header: t("common:period"),
          cell: (cell) =>
            cell.row.original.isGroup
              ? renderGroupTitle(cell.row.original)
              : renderTitleCell({
                  text: cell.row.original.title || "",
                  clampClass: "line-clamp-3",
                  showJobIcon: filter.group_by == ApiAnalyticsGroupBy.job,
                  job: cell.row.original.job,
                  link: cell.row.original.link,
                  onClick: cell.row.original.onClick,
                  tooltip:
                    filter.group_by == ApiAnalyticsGroupBy.job
                      ? renderCreatedTooltip(cell.row.original.title || "", cell.row.original.job)
                      : undefined,
                }),
          footer: totalFooter,
          ...ColumnSizes.stageFlowTitle,
        })
      );
    }

    if (report?.config && report.config.stages.length > 0) {
      for (let i = 0; i < report.config.stages.length; i++) {
        const stage = report.config.stages[i];

        const footerValue = allStagesFlatMap
          .filter((y) => y.pipeline_stage.id == stage.id)
          .reduce((acc, y) => acc + y.value, 0);

        const applicantsIds = allStagesFlatMap
          .filter((y) => y.pipeline_stage.id == stage.id)
          .flatMap((y) => y.applicants_ids);

        inner.push(
          columnHelper.display({
            id: `stage-${i}`,
            header: stage.title,
            cell: (cell) => {
              const stageData = cell.row.original.stages.find(
                (x) => x.pipeline_stage.id == stage.id
              );
              const value = stageData?.value || 0;
              const conversion = stageData?.conversion;
              const isConversionMode = filter.value_mode === ApiAnalyticsValueMode.conversion;

              const displayValue = isConversionMode
                ? conversion != null
                  ? `${conversion}%`
                  : "-"
                : value || "-";

              const tooltipValue = isConversionMode
                ? value.toString()
                : conversion != null
                  ? `${conversion}%`
                  : null;

              const stageContent = (
                <>
                  {value > 0 ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <LinkButtonTo
                          variant={"secondary"}
                          className={"text-default pl-2"}
                          onClick={() => {
                            if (stageData)
                              handlePreviewApplicants(
                                stageData.applicants_ids,
                                AnalyticReportTypes.recruitingStageFlow
                              );
                          }}
                        >
                          {displayValue}
                        </LinkButtonTo>
                      </TooltipTrigger>
                      {tooltipValue && <TooltipContent>{tooltipValue}</TooltipContent>}
                    </Tooltip>
                  ) : (
                    ""
                  )}

                  {!cell.row.original.isGroup && !value ? (
                    <span className={"text-dark/40 pl-2"}>-</span>
                  ) : (
                    ""
                  )}
                </>
              );

              return cell.row.original.isGroup ? stageContent : cellWrap(stageContent);
            },
            footer: () =>
              isTotalMode ? (
                <Box className="px-2 pt-8">
                  {footerValue > 0 ? (
                    <LinkButtonTo
                      variant={"secondary"}
                      className={"text-default pl-2"}
                      onClick={() => {
                        if (applicantsIds)
                          handlePreviewApplicants(
                            applicantsIds,
                            AnalyticReportTypes.recruitingStageFlow
                          );
                      }}
                    >
                      {footerValue}
                    </LinkButtonTo>
                  ) : (
                    ""
                  )}
                </Box>
              ) : undefined,
            enableResizing: false,
            ...ColumnSizes.stageFlowStage,
          })
        );
      }
    }

    if (filter.value_mode === ApiAnalyticsValueMode.funnel && filter.end_stage_id != null) {
      const endStageId = filter.end_stage_id;

      inner.push(
        columnHelper.display({
          id: "conversion",
          header: t("common:conversion"),
          cell: (cell) => {
            if (cell.row.original.isGroup) return null;
            const stageData = cell.row.original.stages.find(
              (x) => x.pipeline_stage.id === endStageId
            );
            const conversion = stageData?.conversion;
            return cellWrap(
              conversion != null ? (
                <span className="pl-2">{conversion}%</span>
              ) : (
                <span className="text-dark/40 pl-2">-</span>
              )
            );
          },
          enableResizing: false,
          ...ColumnSizes.stageFlowStage,
        })
      );

      inner.push(
        columnHelper.display({
          id: "avg_days",
          header: `${t("ats:avg_time")}, ${t("common:days")}`,
          cell: (cell) => {
            if (cell.row.original.isGroup) return null;
            const stageData = cell.row.original.stages.find(
              (x) => x.pipeline_stage.id === endStageId
            );
            const avgDays = stageData?.avg_days;
            return cellWrap(
              avgDays != null ? (
                <span className="pl-2">{avgDays}</span>
              ) : (
                <span className="text-dark/40 pl-2">-</span>
              )
            );
          },
          enableResizing: false,
          ...ColumnSizes.stageFlowStage,
        })
      );
    }
    /*  нужно чтобы таблица не растягивала столбцы на всю ширину */
    inner.push(
      columnHelper.display({
        id: "spacer",
        header: "",
        cell: () => null,
        footer: () => null,
        enableResizing: false,
        size: undefined,
        minSize: undefined,
        maxSize: undefined,
      })
    );

    return inner;
  }, [reportCalculated, filter.group_by, filter.value_mode, filter.end_stage_id]);

  return (
    <Table
      data={reportCalculated || []}
      columns={columns}
      showSkeleton={isLoading}
      stickyHeader={true}
      stickyFirstColumn={true}
      stickyFooter={true}
      highlightColumnOnHover={true}
      layout={"fixed"}
      cellPaddingY={false}
    />
  );
};
